/**
 * @engine/render2d - Post-Process Uniform Layouts
 * 
 * Pre-calculated layouts for all post-process effect uniform buffers.
 * These match the WGSL struct definitions in shaders/postprocess/*.wgsl
 */

import { defineStruct, GPUType } from "../core/GPUTypes";


// ============================================================================
// VIGNETTE
// ============================================================================

/**
 * Matches vignette.wgsl VignetteUniforms struct:
 * struct VignetteUniforms {
 *   intensity: f32,   // 0
 *   smoothness: f32,  // 4
 *   roundness: f32,   // 8
 *   _pad: f32,        // offset 12 (padding for vec2 alignment)
 *   center: vec2f,    // offset 16 (vec2 aligns to 8)
 * } // size: 24
 */
export const VIGNETTE_LAYOUT = defineStruct('VignetteUniforms', {
  intensity: GPUType.f32().build(),
  smoothness: GPUType.f32().build(),
  roundness: GPUType.f32().build(),
  _pad: GPUType.f32().build(),
  center: GPUType.f32().vec2(),
});

export type VignetteUniforms = typeof VIGNETTE_LAYOUT.types;

// ============================================================================
// CHROMATIC ABERRATION
// ============================================================================

/**
 * Matches chromatic-aberration.wgsl ChromaticUniforms struct
 * Production-quality with spectral sampling
 */
export const CHROMATIC_ABERRATION_LAYOUT = defineStruct('ChromaticUniforms', {
  intensity: GPUType.f32().build(),    // 0 - aberration strength
  falloff: GPUType.f32().build(),      // 4 - edge falloff power
  samples: GPUType.f32().build(),      // 8 - spectral samples (3-8)
  mode: GPUType.f32().build(),         // 12 - 0=radial, 1=barrel, 2=pincushion
  center: GPUType.f32().vec2(),        // 16 - aberration center
  anamorphic: GPUType.f32().vec2(),    // 24 - x/y stretch multipliers
});

export type ChromaticAberrationUniforms = typeof CHROMATIC_ABERRATION_LAYOUT.types;

// ============================================================================
// COLOR GRADING
// ============================================================================

/**
 * Matches color-grading.wgsl ColorGradingUniforms struct
 */
export const COLOR_GRADING_LAYOUT = defineStruct('ColorGradingUniforms', {
  exposure: GPUType.f32().build(),     // 0
  contrast: GPUType.f32().build(),     // 4
  saturation: GPUType.f32().build(),   // 8
  temperature: GPUType.f32().build(),  // 12
  tint: GPUType.f32().build(),         // 16
  hueShift: GPUType.f32().build(),     // 20
  _pad: GPUType.f32().vec2(),          // 24
  colorFilter: GPUType.f32().vec3(),   // 32
  _pad2: GPUType.f32().build(),        // 44
});

export type ColorGradingUniforms = typeof COLOR_GRADING_LAYOUT.types;

// ============================================================================
// LENS DISTORTION
// ============================================================================

/**
 * Matches lens-distortion.wgsl LensUniforms struct
 */
export const LENS_DISTORTION_LAYOUT = defineStruct('LensUniforms', {
  intensity: GPUType.f32().build(),   // 0
  scale: GPUType.f32().build(),       // 4
  center: GPUType.f32().vec2(),       // 8
});

export type LensDistortionUniforms = typeof LENS_DISTORTION_LAYOUT.types;

// ============================================================================
// FILM GRAIN
// ============================================================================

/**
 * Matches film-grain.wgsl GrainUniforms struct
 */
export const FILM_GRAIN_LAYOUT = defineStruct('GrainUniforms', {
  intensity: GPUType.f32().build(),   // 0
  response: GPUType.f32().build(),    // 4
  time: GPUType.f32().build(),        // 8
  _pad: GPUType.f32().build(),        // 12
});

export type FilmGrainUniforms = typeof FILM_GRAIN_LAYOUT.types;

// ============================================================================
// CHANNEL MIXER
// ============================================================================

/**
 * Matches channel-mixer.wgsl ChannelMixerUniforms struct
 */
export const CHANNEL_MIXER_LAYOUT = defineStruct('ChannelMixerUniforms', {
  redOut: GPUType.f32().vec3(),       // 0 (aligned to 16)
  _pad0: GPUType.f32().build(),       // 12
  greenOut: GPUType.f32().vec3(),     // 16
  _pad1: GPUType.f32().build(),       // 28
  blueOut: GPUType.f32().vec3(),      // 32
  _pad2: GPUType.f32().build(),       // 44
});

export type ChannelMixerUniforms = typeof CHANNEL_MIXER_LAYOUT.types;

// ============================================================================
// LIFT/GAMMA/GAIN
// ============================================================================

/**
 * Matches lift-gamma-gain.wgsl LGGUniforms struct
 */
export const LIFT_GAMMA_GAIN_LAYOUT = defineStruct('LGGUniforms', {
  lift: GPUType.f32().vec3(),         // 0 (aligned to 16)
  _pad0: GPUType.f32().build(),       // 12
  gamma: GPUType.f32().vec3(),        // 16
  _pad1: GPUType.f32().build(),       // 28
  gain: GPUType.f32().vec3(),         // 32
  _pad2: GPUType.f32().build(),       // 44
});

export type LiftGammaGainUniforms = typeof LIFT_GAMMA_GAIN_LAYOUT.types;

// ============================================================================
// MOTION BLUR
// ============================================================================

/**
 * Matches motion-blur.wgsl MotionBlurUniforms struct
 */
export const MOTION_BLUR_LAYOUT = defineStruct('MotionBlurUniforms', {
  velocity: GPUType.f32().vec2(),       // 0
  intensity: GPUType.f32().build(),     // 8
  samples: GPUType.u32().build(),       // 12
  minVelocity: GPUType.f32().build(),   // 16
  maxVelocity: GPUType.f32().build(),   // 20
  _pad0: GPUType.f32().build(),         // 24
  _pad1: GPUType.f32().build(),         // 28
});

export type MotionBlurUniforms = typeof MOTION_BLUR_LAYOUT.types;

// ============================================================================
// WHITE BALANCE
// ============================================================================

/**
 * Matches white-balance.wgsl WhiteBalanceUniforms struct
 */
export const WHITE_BALANCE_LAYOUT = defineStruct('WhiteBalanceUniforms', {
  temperature: GPUType.f32().build(), // 0
  tint: GPUType.f32().build(),        // 4
  _pad: GPUType.f32().vec2(),         // 8
});

export type WhiteBalanceUniforms = typeof WHITE_BALANCE_LAYOUT.types;

// ============================================================================
// BLOOM (Progressive Mip Bloom - 4 pass types)
// ============================================================================

/**
 * Matches bloom-threshold.wgsl ThresholdUniforms struct (Pass 1)
 */
export const BLOOM_THRESHOLD_LAYOUT = defineStruct('ThresholdUniforms', {
  threshold: GPUType.f32().build(),     // 0
  softThreshold: GPUType.f32().build(), // 4
  _pad0: GPUType.f32().build(),         // 8
  _pad1: GPUType.f32().build(),         // 12
});

export type BloomThresholdUniforms = typeof BLOOM_THRESHOLD_LAYOUT.types;

/**
 * Matches bloom-downsample.wgsl DownsampleUniforms struct
 */
export const BLOOM_DOWNSAMPLE_LAYOUT = defineStruct('DownsampleUniforms', {
  texelSize: GPUType.f32().vec2(),      // 0
  mipLevel: GPUType.f32().build(),      // 8
  anamorphic: GPUType.f32().build(),    // 12 - stretch ratio (-1 to 1)
});

export type BloomDownsampleUniforms = typeof BLOOM_DOWNSAMPLE_LAYOUT.types;

/**
 * Matches bloom-upsample.wgsl UpsampleUniforms struct
 */
export const BLOOM_UPSAMPLE_LAYOUT = defineStruct('UpsampleUniforms', {
  texelSize: GPUType.f32().vec2(),      // 0
  scatter: GPUType.f32().build(),       // 8 - radius multiplier for tent filter
  weight: GPUType.f32().build(),        // 12 - energy conservation weight
});

export type BloomUpsampleUniforms = typeof BLOOM_UPSAMPLE_LAYOUT.types;

/**
 * Legacy blur layout (kept for compatibility)
 */
export const BLOOM_BLUR_LAYOUT = defineStruct('BlurUniforms', {
  texelSize: GPUType.f32().vec2(),      // 0
  horizontal: GPUType.u32().build(),    // 8
  _pad: GPUType.u32().build(),          // 12
});

export type BloomBlurUniforms = typeof BLOOM_BLUR_LAYOUT.types;

/**
 * Matches bloom-composite.wgsl CompositeUniforms struct
 * struct CompositeUniforms {
 *   intensity: f32,           // 0
 *   dirtIntensity: f32,       // 4
 *   saturation: f32,          // 8
 *   chromaticShift: f32,      // 12
 *   tint: vec3f,              // 16 (aligned to 16)
 *   clampMax: f32,            // 28
 *   bloomBlendMode: f32,      // 32
 *   _pad: vec3f,              // 36
 * } // size: 48
 */
export const BLOOM_COMPOSITE_LAYOUT = defineStruct('CompositeUniforms', {
  intensity: GPUType.f32().build(),
  dirtIntensity: GPUType.f32().build(),
  saturation: GPUType.f32().build(),
  chromaticShift: GPUType.f32().build(),
  tint: GPUType.f32().vec3(),
  clampMax: GPUType.f32().build(),
  bloomBlendMode: GPUType.f32().build(),
  _pad0: GPUType.f32().build(),
  _pad1: GPUType.f32().build(),
  _pad2: GPUType.f32().build(),
});

export type BloomCompositeUniforms = typeof BLOOM_COMPOSITE_LAYOUT.types;

// ============================================================================
// LENS FLARE (4 passes)
// ============================================================================

/**
 * Matches lens-flare-threshold.wgsl ThresholdUniforms struct (Pass 1)
 * Production-quality with Karis average
 */
export const LENS_FLARE_THRESHOLD_LAYOUT = defineStruct('LensFlareThresholdUniforms', {
  threshold: GPUType.f32().build(),   // 0
  softKnee: GPUType.f32().build(),    // 4
  anamorphic: GPUType.f32().build(),  // 8 - Pre-stretch (-1 to 1)
  _pad: GPUType.f32().build(),        // 12
});

export type LensFlareThresholdUniforms = typeof LENS_FLARE_THRESHOLD_LAYOUT.types;

/**
 * Matches lens-flare-flares.wgsl FlaresUniforms struct (Pass 2)
 * Production-quality with iris shapes and halo ring
 * 
 * struct FlaresUniforms {
 *   intensity: f32,           // 0
 *   ghostCount: u32,          // 4
 *   ghostSpacing: f32,        // 8
 *   ghostScale: f32,          // 12
 *   chromaticAmount: f32,     // 16
 *   haloRadius: f32,          // 20
 *   haloWidth: f32,           // 24
 *   haloIntensity: f32,       // 28
 *   anamorphicX: f32,         // 32
 *   anamorphicY: f32,         // 36
 *   irisShape: f32,           // 40
 *   irisRotation: f32,        // 44
 *   tintColor: vec3f,         // 48
 *   vignetteEffect: f32,      // 60
 * } // size: 64
 */
export const LENS_FLARE_FLARES_LAYOUT = defineStruct('LensFlareFlaresUniforms', {
  intensity: GPUType.f32().build(),           // 0
  ghostCount: GPUType.u32().build(),          // 4
  ghostSpacing: GPUType.f32().build(),        // 8
  ghostScale: GPUType.f32().build(),          // 12
  chromaticAmount: GPUType.f32().build(),     // 16
  haloRadius: GPUType.f32().build(),          // 20
  haloWidth: GPUType.f32().build(),           // 24
  haloIntensity: GPUType.f32().build(),       // 28
  anamorphicX: GPUType.f32().build(),         // 32
  anamorphicY: GPUType.f32().build(),         // 36
  irisShape: GPUType.f32().build(),           // 40
  irisRotation: GPUType.f32().build(),        // 44
  tintColor: GPUType.f32().vec3(),            // 48
  vignetteEffect: GPUType.f32().build(),      // 60
});

export type LensFlareFlaresUniforms = typeof LENS_FLARE_FLARES_LAYOUT.types;

/**
 * Matches lens-flare-streaks.wgsl StreaksUniforms struct (Pass 3)
 * Production-quality with multi-directional starburst
 */
export const LENS_FLARE_STREAKS_LAYOUT = defineStruct('LensFlareStreaksUniforms', {
  intensity: GPUType.f32().build(),     // 0
  length: GPUType.f32().build(),        // 4
  threshold: GPUType.f32().build(),     // 8
  samples: GPUType.u32().build(),       // 12
  rayCount: GPUType.u32().build(),      // 16
  rotation: GPUType.f32().build(),      // 20
  anamorphicX: GPUType.f32().build(),   // 24
  anamorphicY: GPUType.f32().build(),   // 28
  falloffExponent: GPUType.f32().build(), // 32
  chromatic: GPUType.f32().build(),     // 36
  _pad0: GPUType.f32().build(),         // 40
  _pad1: GPUType.f32().build(),         // 44
});

export type LensFlareStreaksUniforms = typeof LENS_FLARE_STREAKS_LAYOUT.types;

/**
 * Matches lens-flare-composite.wgsl CompositeUniforms struct (Pass 4)
 * Production-quality with blend modes and lens dirt
 */
export const LENS_FLARE_COMPOSITE_LAYOUT = defineStruct('LensFlareCompositeUniforms', {
  intensity: GPUType.f32().build(),           // 0
  flaresContribution: GPUType.f32().build(),  // 4
  streaksContribution: GPUType.f32().build(), // 8
  blendMode: GPUType.f32().build(),           // 12 - 0=additive, 1=screen, 2=soft
  saturation: GPUType.f32().build(),          // 16
  temperature: GPUType.f32().build(),         // 20
  dirtIntensity: GPUType.f32().build(),       // 24
  _pad: GPUType.f32().build(),                // 28
});

export type LensFlareCompositeUniforms = typeof LENS_FLARE_COMPOSITE_LAYOUT.types;

// ============================================================================
// AQUA (WATERCOLOR) EFFECT LAYOUTS
// ============================================================================

/**
 * Matches aqua.wgsl AquaUniforms struct
 * Watercolor/painterly post-process effect
 */
export const AQUA_LAYOUT = defineStruct('AquaUniforms', {
  // Core (16 bytes)
  opacity: GPUType.f32().build(),           // 0  - Effect intensity (0-1)
  interval: GPUType.f32().build(),          // 4  - Gradient sampling interval
  blurWidth: GPUType.f32().build(),         // 8  - Fill blur width multiplier
  blurFrequency: GPUType.f32().build(),     // 12 - Blur frequency (pre-exponentiated)
  
  // Edge settings (16 bytes) - vec3f + f32 = 16 bytes with padding
  edgeColor: GPUType.f32().vec3(),          // 16 - Edge tint color (aligned to 16)
  edgeContrast: GPUType.f32().build(),      // 28 - Edge contrast (0.5-4)
  
  // Fill settings (16 bytes)
  fillColor: GPUType.f32().vec3(),          // 32 - Fill tint color (aligned to 16)
  hueShift: GPUType.f32().build(),          // 44 - Hue variation amount (0-1)
  
  // Quality & misc (16 bytes)
  iterations: GPUType.f32().build(),        // 48 - Number of blur iterations (4-32)
  edgeOpacity: GPUType.f32().build(),       // 52 - Edge color mix amount (0-1)
  fillOpacity: GPUType.f32().build(),       // 56 - Fill color mix amount (0-1)
  overlayMode: GPUType.f32().build(),       // 60 - 0=off, 1=multiply, 2=overlay, 3=screen
  
  // Screen info (16 bytes)
  aspectRatio: GPUType.f32().build(),       // 64 - Width / Height
  overlayOpacity: GPUType.f32().build(),    // 68 - Paper texture overlay opacity
  noiseScale: GPUType.f32().build(),        // 72 - Granulation noise scale
  _pad: GPUType.f32().build(),              // 76
});

export type AquaUniforms = typeof AQUA_LAYOUT.types;

// ============================================================================
// TONEMAP
// ============================================================================

/**
 * Matches tonemap.slang TonemapUniforms struct
 */
export const TONEMAP_LAYOUT = defineStruct('TonemapUniforms', {
  exposure: GPUType.f32().build(),        // 0 - EV adjustment
  mode: GPUType.f32().build(),            // 4 - operator (0=Reinhard … 5=PBR Neutral)
  whitePoint: GPUType.f32().build(),      // 8 - Reinhard-extended / Hable white point
  ditherStrength: GPUType.f32().build(),  // 12 - LDR output IGN dither amplitude (≈1/255; 0 = off)
});

export type TonemapUniforms = typeof TONEMAP_LAYOUT.types;

// ============================================================================
// GOD RAYS (3 passes)
// ============================================================================

/**
 * Matches god-rays-mask.slang GodRaysMaskUniforms struct (Pass 1)
 */
export const GOD_RAYS_MASK_LAYOUT = defineStruct('GodRaysMaskUniforms', {
  lightPos: GPUType.f32().vec2(),      // 0
  threshold: GPUType.f32().build(),    // 8
  softKnee: GPUType.f32().build(),     // 12
  radiusStart: GPUType.f32().build(),  // 16
  radiusEnd: GPUType.f32().build(),    // 20
  aspect: GPUType.f32().build(),       // 24
  _pad: GPUType.f32().build(),         // 28
});

export type GodRaysMaskUniforms = typeof GOD_RAYS_MASK_LAYOUT.types;

/**
 * Matches god-rays-blur.slang GodRaysBlurUniforms struct (Pass 2..N)
 */
export const GOD_RAYS_BLUR_LAYOUT = defineStruct('GodRaysBlurUniforms', {
  lightPos: GPUType.f32().vec2(),      // 0
  stepScale: GPUType.f32().build(),    // 8  - density scaled by samples^-pass
  decay: GPUType.f32().build(),        // 12
  weight: GPUType.f32().build(),       // 16
  samples: GPUType.f32().build(),      // 20
  dither: GPUType.f32().build(),       // 24
  _pad: GPUType.f32().build(),         // 28
});

export type GodRaysBlurUniforms = typeof GOD_RAYS_BLUR_LAYOUT.types;

/**
 * Matches god-rays-composite.slang GodRaysCompositeUniforms struct (final pass)
 */
export const GOD_RAYS_COMPOSITE_LAYOUT = defineStruct('GodRaysCompositeUniforms', {
  intensity: GPUType.f32().build(),    // 0
  exposure: GPUType.f32().build(),     // 4
  blendMode: GPUType.f32().build(),    // 8  - 0=additive, 1=screen
  _pad0: GPUType.f32().build(),        // 12
  tint: GPUType.f32().vec3(),          // 16
  _pad1: GPUType.f32().build(),        // 28
});

export type GodRaysCompositeUniforms = typeof GOD_RAYS_COMPOSITE_LAYOUT.types;

// ============================================================================
// KUWAHARA (oil painting)
// ============================================================================

/**
 * Matches kuwahara.slang KuwaharaUniforms struct
 */
export const KUWAHARA_LAYOUT = defineStruct('KuwaharaUniforms', {
  radius: GPUType.f32().build(),       // 0 - window radius in pixels
  sharpness: GPUType.f32().build(),    // 4 - variance exponent q
  blend: GPUType.f32().build(),        // 8 - mix with source
  _pad: GPUType.f32().build(),         // 12
});

export type KuwaharaUniforms = typeof KUWAHARA_LAYOUT.types;

// ============================================================================
// CRT
// ============================================================================

/**
 * Matches crt.slang CRTUniforms struct
 */
export const CRT_LAYOUT = defineStruct('CRTUniforms', {
  curvature: GPUType.f32().build(),          // 0
  scanlineIntensity: GPUType.f32().build(),  // 4
  scanlineCount: GPUType.f32().build(),      // 8
  maskIntensity: GPUType.f32().build(),      // 12
  chromatic: GPUType.f32().build(),          // 16
  flicker: GPUType.f32().build(),            // 20
  noise: GPUType.f32().build(),              // 24
  vignette: GPUType.f32().build(),           // 28
  cornerRadius: GPUType.f32().build(),       // 32
  brightness: GPUType.f32().build(),         // 36
  time: GPUType.f32().build(),               // 40
  _pad: GPUType.f32().build(),               // 44
});

export type CRTUniforms = typeof CRT_LAYOUT.types;

// ============================================================================
// HEAT HAZE
// ============================================================================

/**
 * Matches heat-haze.slang HeatHazeUniforms struct
 */
export const HEAT_HAZE_LAYOUT = defineStruct('HeatHazeUniforms', {
  strength: GPUType.f32().build(),      // 0
  scale: GPUType.f32().build(),         // 4
  speed: GPUType.f32().build(),         // 8
  verticalFade: GPUType.f32().build(),  // 12
  time: GPUType.f32().build(),          // 16
  aspect: GPUType.f32().build(),        // 20
  _pad: GPUType.f32().vec2(),           // 24
});

export type HeatHazeUniforms = typeof HEAT_HAZE_LAYOUT.types;

// ============================================================================
// GLITCH
// ============================================================================

/**
 * Matches glitch.slang GlitchUniforms struct
 */
export const GLITCH_LAYOUT = defineStruct('GlitchUniforms', {
  lineGlitch: GPUType.f32().build(),   // 0
  blockGlitch: GPUType.f32().build(),  // 4
  rgbSplit: GPUType.f32().build(),     // 8
  speed: GPUType.f32().build(),        // 12
  blockSize: GPUType.f32().build(),    // 16
  time: GPUType.f32().build(),         // 20
  _pad: GPUType.f32().vec2(),          // 24
});

export type GlitchUniforms = typeof GLITCH_LAYOUT.types;

// ============================================================================
// HALFTONE
// ============================================================================

/**
 * Matches halftone.slang HalftoneUniforms struct
 */
export const HALFTONE_LAYOUT = defineStruct('HalftoneUniforms', {
  cellSize: GPUType.f32().build(),     // 0
  angle: GPUType.f32().build(),        // 4
  sharpness: GPUType.f32().build(),    // 8
  mode: GPUType.f32().build(),         // 12
  blend: GPUType.f32().build(),        // 16
  _pad0: GPUType.f32().build(),        // 20
  _pad1: GPUType.f32().build(),        // 24
  _pad2: GPUType.f32().build(),        // 28
  inkColor: GPUType.f32().vec3(),      // 32
  _pad3: GPUType.f32().build(),        // 44
  paperColor: GPUType.f32().vec3(),    // 48
  _pad4: GPUType.f32().build(),        // 60
});

export type HalftoneUniforms = typeof HALFTONE_LAYOUT.types;

// ============================================================================
// PIXEL ART
// ============================================================================

/**
 * Matches pixel-art.slang PixelArtUniforms struct
 */
export const PIXEL_ART_LAYOUT = defineStruct('PixelArtUniforms', {
  pixelSize: GPUType.f32().build(),    // 0
  levels: GPUType.f32().build(),       // 4
  dither: GPUType.f32().build(),       // 8
  paletteMode: GPUType.f32().build(),  // 12
});

export type PixelArtUniforms = typeof PIXEL_ART_LAYOUT.types;

// ============================================================================
// UNDERWATER
// ============================================================================

/**
 * Matches underwater.slang UnderwaterUniforms struct
 */
export const UNDERWATER_LAYOUT = defineStruct('UnderwaterUniforms', {
  waveStrength: GPUType.f32().build(),      // 0
  waveScale: GPUType.f32().build(),         // 4
  causticsIntensity: GPUType.f32().build(), // 8
  causticsScale: GPUType.f32().build(),     // 12
  speed: GPUType.f32().build(),             // 16
  tintStrength: GPUType.f32().build(),      // 20
  time: GPUType.f32().build(),              // 24
  aspect: GPUType.f32().build(),            // 28
  tint: GPUType.f32().vec3(),               // 32
  _pad: GPUType.f32().build(),              // 44
});

export type UnderwaterUniforms = typeof UNDERWATER_LAYOUT.types;

// ============================================================================
// OUTLINE
// ============================================================================

/**
 * Matches outline.slang OutlineUniforms struct
 */
export const OUTLINE_LAYOUT = defineStruct('OutlineUniforms', {
  thickness: GPUType.f32().build(),    // 0
  threshold: GPUType.f32().build(),    // 4
  softness: GPUType.f32().build(),     // 8
  mode: GPUType.f32().build(),         // 12
  edgeColor: GPUType.f32().vec3(),     // 16
  _pad0: GPUType.f32().build(),        // 28
  paperColor: GPUType.f32().vec3(),    // 32
  _pad1: GPUType.f32().build(),        // 44
});

export type OutlineUniforms = typeof OUTLINE_LAYOUT.types;

// ============================================================================
// ZOOM BLUR
// ============================================================================

/**
 * Matches zoom-blur.slang ZoomBlurUniforms struct
 */
export const ZOOM_BLUR_LAYOUT = defineStruct('ZoomBlurUniforms', {
  center: GPUType.f32().vec2(),        // 0
  strength: GPUType.f32().build(),     // 8
  samples: GPUType.f32().build(),      // 12
  startRadius: GPUType.f32().build(),  // 16
  dither: GPUType.f32().build(),       // 20
  aspect: GPUType.f32().build(),       // 24
  _pad: GPUType.f32().build(),         // 28
});

export type ZoomBlurUniforms = typeof ZOOM_BLUR_LAYOUT.types;

// ============================================================================
// LAYOUT REGISTRY
// ============================================================================

export const POST_PROCESS_LAYOUTS = {
  vignette: VIGNETTE_LAYOUT,
  chromaticAberration: CHROMATIC_ABERRATION_LAYOUT,
  colorGrading: COLOR_GRADING_LAYOUT,
  lensDistortion: LENS_DISTORTION_LAYOUT,
  filmGrain: FILM_GRAIN_LAYOUT,
  channelMixer: CHANNEL_MIXER_LAYOUT,
  liftGammaGain: LIFT_GAMMA_GAIN_LAYOUT,
  motionBlur: MOTION_BLUR_LAYOUT,
  whiteBalance: WHITE_BALANCE_LAYOUT,
  bloomThreshold: BLOOM_THRESHOLD_LAYOUT,
  bloomDownsample: BLOOM_DOWNSAMPLE_LAYOUT,
  bloomUpsample: BLOOM_UPSAMPLE_LAYOUT,
  bloomBlur: BLOOM_BLUR_LAYOUT,
  bloomComposite: BLOOM_COMPOSITE_LAYOUT,
  lensFlareThreshold: LENS_FLARE_THRESHOLD_LAYOUT,
  lensFlareFlares: LENS_FLARE_FLARES_LAYOUT,
  lensFlareStreaks: LENS_FLARE_STREAKS_LAYOUT,
  lensFlareComposite: LENS_FLARE_COMPOSITE_LAYOUT,
  aqua: AQUA_LAYOUT,
  tonemap: TONEMAP_LAYOUT,
  godRaysMask: GOD_RAYS_MASK_LAYOUT,
  godRaysBlur: GOD_RAYS_BLUR_LAYOUT,
  godRaysComposite: GOD_RAYS_COMPOSITE_LAYOUT,
  kuwahara: KUWAHARA_LAYOUT,
  crt: CRT_LAYOUT,
  heatHaze: HEAT_HAZE_LAYOUT,
  glitch: GLITCH_LAYOUT,
  halftone: HALFTONE_LAYOUT,
  pixelArt: PIXEL_ART_LAYOUT,
  underwater: UNDERWATER_LAYOUT,
  outline: OUTLINE_LAYOUT,
  zoomBlur: ZOOM_BLUR_LAYOUT,
} as const;
